/**
 * CHG - the change record, and the one rule that makes it HipNumber's rather
 * than a generic ticket: the SEV1 freeze.
 *
 * Types (design doc section 32):
 *   STANDARD   pre-approved, repeatable, low risk        skips approval
 *   NORMAL     needs review and approval                 must pass AWAITING_APPROVAL
 *   EMERGENCY  restores or protects production service   skips approval, linked to an incident
 *
 * The freeze (section 37): while any SEV1 incident is active, no change may
 * enter IMPLEMENTING unless it is an EMERGENCY change linked to one of those
 * incidents. Not NORMAL only, and not "unless somebody overrides": a SEV1 is
 * by definition the moment a routine deploy is most likely to make things
 * worse, and the way to get an urgent fix through is to say so - reclassify
 * it EMERGENCY and link it - which is exactly the record the postmortem needs.
 *
 * `changeFreeze` is pure so it can be tested without a database and so the
 * detail screen can show the operator the verdict BEFORE they press the
 * button, rather than as a 409 afterwards.
 */

export const CHANGE_TYPES = ['STANDARD', 'NORMAL', 'EMERGENCY'] as const;
export type ChangeType = (typeof CHANGE_TYPES)[number];

/** Operator-declared in Phase 1. A computed risk score is Phase 5 (section 38). */
export const CHANGE_RISKS = ['LOW', 'MEDIUM', 'HIGH'] as const;
export type ChangeRisk = (typeof CHANGE_RISKS)[number];

export const CHANGE_STATUSES = [
  'DRAFT', 'ASSESSMENT', 'AWAITING_APPROVAL', 'APPROVED', 'SCHEDULED', 'IMPLEMENTING', 'VALIDATING',
  'COMPLETED', 'FAILED', 'ROLLED_BACK', 'CANCELED',
] as const;
export type ChangeStatus = (typeof CHANGE_STATUSES)[number];

const BASE_TRANSITIONS: Readonly<Record<ChangeStatus, readonly ChangeStatus[]>> = {
  DRAFT: ['ASSESSMENT', 'AWAITING_APPROVAL', 'CANCELED'],
  ASSESSMENT: ['AWAITING_APPROVAL', 'DRAFT', 'CANCELED'],
  AWAITING_APPROVAL: ['APPROVED', 'DRAFT', 'CANCELED'],
  APPROVED: ['SCHEDULED', 'IMPLEMENTING', 'CANCELED'],
  SCHEDULED: ['IMPLEMENTING', 'APPROVED', 'CANCELED'],
  IMPLEMENTING: ['VALIDATING', 'FAILED', 'ROLLED_BACK'],
  VALIDATING: ['COMPLETED', 'FAILED', 'ROLLED_BACK'],
  COMPLETED: [],
  FAILED: ['ROLLED_BACK'],
  ROLLED_BACK: [],
  CANCELED: [],
};

/** Pre-approved kinds may go straight from DRAFT to APPROVED. NORMAL may not. */
export function changeTransitions(type: ChangeType, from: ChangeStatus): readonly ChangeStatus[] {
  const base = BASE_TRANSITIONS[from];
  if (from === 'DRAFT' && type !== 'NORMAL') return [...base, 'APPROVED'];
  return base;
}

export function canTransitionChange(type: ChangeType, from: ChangeStatus, to: ChangeStatus): boolean {
  return changeTransitions(type, from).includes(to);
}

export const CHANGE_TERMINAL_STATUSES: readonly ChangeStatus[] = ['COMPLETED', 'FAILED', 'ROLLED_BACK', 'CANCELED'];
export const CHANGE_IN_FLIGHT_STATUSES: readonly ChangeStatus[] = ['IMPLEMENTING', 'VALIDATING'];

export function isChangeOpen(status: ChangeStatus): boolean {
  return !CHANGE_TERMINAL_STATUSES.includes(status);
}

export const CHANGE_STATUS_TIMESTAMP: Readonly<Partial<Record<ChangeStatus, 'startedAt' | 'finishedAt'>>> = {
  IMPLEMENTING: 'startedAt',
  COMPLETED: 'finishedAt',
  FAILED: 'finishedAt',
  ROLLED_BACK: 'finishedAt',
  CANCELED: 'finishedAt',
};

/** The freeze gates exactly one door: the one into production. */
export function freezeApplies(to: ChangeStatus): boolean {
  return to === 'IMPLEMENTING';
}

export interface FreezeInput {
  readonly changeType: ChangeType;
  /** Numbers of the SEV1 incidents currently active (not RESOLVED / CLOSED). */
  readonly activeSev1: readonly string[];
  /** Incident numbers this change is linked to, in either direction. */
  readonly linkedIncidents: readonly string[];
}

export interface FreezeVerdict {
  readonly blocked: boolean;
  /** Human sentence for the screen and the 409 body; null when not blocked. */
  readonly reason: string | null;
  /** The SEV1 incidents doing the blocking, so the screen can link them. */
  readonly incidents: readonly string[];
}

export function changeFreeze(input: FreezeInput): FreezeVerdict {
  const incidents = [...input.activeSev1];
  if (incidents.length === 0) return { blocked: false, reason: null, incidents };
  if (input.changeType === 'EMERGENCY') {
    const linked = incidents.filter((n) => input.linkedIncidents.includes(n));
    if (linked.length > 0) return { blocked: false, reason: null, incidents };
    return {
      blocked: true,
      incidents,
      reason: `A SEV1 incident is active (${incidents.join(', ')}). An EMERGENCY change may proceed only when it is linked to one of them.`,
    };
  }
  return {
    blocked: true,
    incidents,
    reason: `A SEV1 incident is active (${incidents.join(', ')}). ${input.changeType} changes are frozen; reclassify this change as EMERGENCY and link it to the incident if it is part of the response.`,
  };
}
